import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useState, useCallback } from 'react';
import { fetchVoiceCallAggregates } from './voice-calls-api';
import { daysToDateRange } from './voice-utils';

const PAGE_SIZE = 25;

export function useVoiceTenantCallAggregates(selectedDays: number = 0) {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageCursors, setPageCursors] = useState<Record<number, string | undefined>>({});

  // Reset pagination when selectedDays changes (adjust state during render)
  // https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
  const [prevSelectedDays, setPrevSelectedDays] = useState(selectedDays);
  if (selectedDays !== prevSelectedDays) {
    setPrevSelectedDays(selectedDays);
    setCurrentPage(1);
    setPageCursors({});
  }

  const cursor = pageCursors[currentPage];
  const dateRange = daysToDateRange(selectedDays);

  const query = useQuery({
    queryKey: ['voiceCallAggregates', selectedDays, currentPage, cursor],
    queryFn: () =>
      fetchVoiceCallAggregates({
        cursor,
        limit: PAGE_SIZE,
        ...dateRange,
      }),
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });

  const goToNextPage = useCallback(() => {
    if (query.data?.hasNextPage && query.data.cursor) {
      setPageCursors(prev => ({
        ...prev,
        [currentPage + 1]: query.data!.cursor,
      }));
      setCurrentPage(p => p + 1);
    }
  }, [query.data, currentPage]);

  const goToPreviousPage = useCallback(() => {
    if (currentPage > 1) {
      setCurrentPage(p => p - 1);
    }
  }, [currentPage]);

  return {
    data: query.data?.data ?? [],
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    hasNextPage: query.data?.hasNextPage ?? false,
    currentPage,
    goToNextPage,
    goToPreviousPage,
  };
}
